import React, { useState } from "react";
import styled from "@emotion/styled";
import { Outlet } from "react-router-dom";

import { Box, CssBaseline, IconButton, Paper as MuiPaper } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import useMediaQuery from "@mui/material/useMediaQuery";
import { spacing } from "@mui/system";
import { ChevronLeft, ChevronRight } from "@mui/icons-material";

import GlobalStyle from "../components/GlobalStyle";
import { useDashboardItems } from "../components/sidebar/dashboardItems";
import Sidebar from "../components/sidebar/Sidebar";
import Footer from "../components/Footer";

const drawerWidth = 258;
const collapsedDrawerWidth = 64;

const Root = styled.div`
  display: flex;
  min-height: 100vh;
`;

const Drawer = styled.div<{ collapsed?: boolean }>`
  ${(props) => props.theme.breakpoints.up("md")} {
    width: ${(props) =>
      props.collapsed ? collapsedDrawerWidth : drawerWidth}px;
    flex-shrink: 0;
    transition: width 0.2s ease-in-out;
  }
`;

const AppContent = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  max-width: 100%;
`;

const CollapseButton = styled(IconButton)`
  position: fixed;
  bottom: 70px;
  z-index: 1201;
  background-color: ${(props) => props.theme.palette.primary.main};
  color: white;
  width: 24px;
  height: 24px;
  &:hover {
    background-color: ${(props) => props.theme.palette.primary.dark};
  }
`;

const Paper = styled(MuiPaper)(spacing);

const MainContent = styled(Paper)`
  flex: 1;
  background: ${(props) => props.theme.palette.background.default};
  border-radius: 0;

  @media all and (-ms-high-contrast: none), (-ms-high-contrast: active) {
    flex: none;
  }

  .MuiPaper-root .MuiPaper-root {
    box-shadow: none;
  }
`;

interface DashboardType {
  children?: React.ReactNode;
}

const Dashboard: React.FC<DashboardType> = ({ children }) => {
  const dashboardItems = useDashboardItems();
  const [collapsed, setCollapsed] = useState(false);

  const theme = useTheme();
  const isLgUp = useMediaQuery(theme.breakpoints.up("lg"));

  const toggleCollapsed = () => {
    setCollapsed(!collapsed);
  };

  return (
    <Root>
      <CssBaseline />
      <GlobalStyle />
      <Drawer collapsed={collapsed}>
        <Box sx={{ display: { xs: "none", md: "block" } }}>
          <Sidebar
            PaperProps={{
              style: {
                width: collapsed ? collapsedDrawerWidth : drawerWidth,
                transition: "width 0.2s ease-in-out",
              },
            }}
            items={dashboardItems}
            collapsed={collapsed}
          />
        </Box>
        <CollapseButton
          onClick={toggleCollapsed}
          size="small"
          sx={{
            left: collapsed ? collapsedDrawerWidth - 12 : drawerWidth - 12,
            transition: "left 0.2s ease-in-out",
            display: { xs: "none", md: "flex" },
          }}
        >
          {collapsed ? <ChevronRight /> : <ChevronLeft />}
        </CollapseButton>
      </Drawer>
      <AppContent>
        <MainContent p={isLgUp ? 12 : 5} elevation={0}>
          {children}
          <Outlet />
        </MainContent>
        <Footer />
      </AppContent>
    </Root>
  );
};

export default Dashboard;
